/**
 * BootSequence.jsx
 * Full-screen schematic boot animation.
 * Phases: concentric rings draw on → glowing arc → telemetry tickers → progress arc → curtain wipe.
 * Calls onComplete() when the curtain finishes.
 */
import { useEffect, useRef, useState, useCallback } from "react";

const TEAL   = "#36c4a8";
const TEAL2  = "#24826e";
const FAINT  = "rgba(54,196,168,0.18)";
const BG     = "#0a1210";
const MONO   = "'DM Mono', monospace";

const PHASES = [
  "Booting",
  "Linking nodes",
  "Calibrating optics",
  "Aligning telemetry",
  "Synchronising grid",
  "Handshake complete",
];

// Duration each "tick" in ms
const RING_INTERVAL  = 200;   // one ring per interval
const TICKER_INTERVAL = 80;   // coord flicker
const ARC_DURATION   = 1800;  // progress arc fill
const PHASE_INTERVAL = 340;   // phase label cycle
const WIPE_DURATION  = 900;

export default function BootSequence({ onComplete }) {
  const canvasRef  = useRef(null);
  const stateRef   = useRef({
    rings:    0,   // 0-6
    arcT:     0,   // 0-1
    phase:    0,
    wipeT:    0,   // 0-1
    wiping:   false,
    done:     false,
    lat:      "N 40°43.22′",
    lon:      "W 74°00.18′",
    session:  "SES-" + Math.floor(Math.random() * 9000 + 1000),
  });
  const rafRef     = useRef(null);
  const startRef   = useRef(null);
  const [phase, setPhase] = useState(0);

  // ── canvas draw loop ──────────────────────────────────────
  const draw = useCallback((now) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx  = canvas.getContext("2d");
    const W    = canvas.width;
    const H    = canvas.height;
    const cx   = W / 2;
    const cy   = H / 2;
    const s    = stateRef.current;

    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    if (s.wiping) {
      // Curtain wipe: solid panel sweeps from top to bottom
      const wipeH = s.wipeT * H;
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, W, wipeH);
      // Draw already-revealed content below the wipe line
      if (s.wipeT < 1) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, wipeH, W, H - wipeH);
        ctx.clip();
        drawContent(ctx, W, H, cx, cy, s, now);
        ctx.restore();
      }
    } else {
      drawContent(ctx, W, H, cx, cy, s, now);
    }

    if (!s.done) {
      rafRef.current = requestAnimationFrame(draw);
    }
  }, []);

  function drawContent(ctx, W, H, cx, cy, s, now) {
    const maxR = Math.min(W, H) * 0.38;

    // ── blueprint grid ──────────────────────────────────────
    ctx.strokeStyle = "rgba(54,196,168,0.04)";
    ctx.lineWidth   = 0.5;
    const spacing = 60;
    for (let x = 0; x < W; x += spacing) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = 0; y < H; y += spacing) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }

    // ── concentric rings (draw on progressively) ────────────
    const ringRadii = [0.08, 0.14, 0.20, 0.27, 0.33, 0.38].map(r => r * Math.min(W, H));
    ringRadii.forEach((r, i) => {
      if (i >= s.rings) return;
      const alpha = 0.08 + (i / ringRadii.length) * 0.22;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(54,196,168,${alpha})`;
      ctx.lineWidth   = i === 0 ? 1.2 : 0.6;
      ctx.setLineDash(i % 2 === 0 ? [] : [4, 8]);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    // ── innermost solid ring accent ─────────────────────────
    if (s.rings >= 2) {
      ctx.beginPath();
      ctx.arc(cx, cy, ringRadii[1], 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(54,196,168,0.55)`;
      ctx.lineWidth   = 1;
      ctx.stroke();
    }

    // ── center dot ──────────────────────────────────────────
    if (s.rings >= 1) {
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fillStyle = TEAL;
      ctx.fill();
      // pulse ring
      const pulse = 0.4 + 0.3 * Math.sin(now * 0.003);
      ctx.beginPath();
      ctx.arc(cx, cy, 10 + pulse * 6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(54,196,168,${0.15 + pulse * 0.15})`;
      ctx.lineWidth   = 1;
      ctx.stroke();
    }

    // ── glowing accent arc ──────────────────────────────────
    if (s.rings >= 4) {
      const arcR = ringRadii[2];
      ctx.beginPath();
      ctx.arc(cx, cy, arcR, -Math.PI * 0.6, Math.PI * 0.1);
      ctx.strokeStyle = TEAL;
      ctx.lineWidth   = 2;
      ctx.shadowBlur  = 14;
      ctx.shadowColor = TEAL;
      ctx.stroke();
      ctx.shadowBlur  = 0;

      // glowing dot at tip
      const tipA = Math.PI * 0.1;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(tipA) * arcR, cy + Math.sin(tipA) * arcR, 4, 0, Math.PI * 2);
      ctx.fillStyle   = TEAL;
      ctx.shadowBlur  = 18;
      ctx.shadowColor = TEAL;
      ctx.fill();
      ctx.shadowBlur  = 0;
    }

    // ── progress arc ────────────────────────────────────────
    const progR  = maxR * 1.05;
    const startA = -Math.PI / 2;
    const endA   = startA + Math.PI * 2 * s.arcT;
    // Track ring
    ctx.beginPath();
    ctx.arc(cx, cy, progR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(54,196,168,0.08)";
    ctx.lineWidth   = 1.5;
    ctx.stroke();

    if (s.arcT > 0) {
      // Filled arc
      ctx.beginPath();
      ctx.arc(cx, cy, progR, startA, endA);
      ctx.strokeStyle = TEAL;
      ctx.lineWidth   = 2;
      ctx.shadowBlur  = 12;
      ctx.shadowColor = TEAL;
      ctx.stroke();
      ctx.shadowBlur  = 0;

      // Leading dot
      ctx.beginPath();
      ctx.arc(
        cx + Math.cos(endA) * progR,
        cy + Math.sin(endA) * progR,
        4.5, 0, Math.PI * 2
      );
      ctx.fillStyle  = TEAL;
      ctx.shadowBlur = 20;
      ctx.shadowColor= TEAL;
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // ── compass ticks around progress ring ──────────────────
    for (let i = 0; i < 72; i++) {
      const a   = (i / 72) * Math.PI * 2 - Math.PI / 2;
      const big = i % 9 === 0;
      const r1  = progR + 5;
      const r2  = progR + (big ? 14 : 8);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
      ctx.strokeStyle = big ? "rgba(54,196,168,0.5)" : "rgba(54,196,168,0.18)";
      ctx.lineWidth   = big ? 1 : 0.5;
      ctx.stroke();
    }

    // ── crosshair ───────────────────────────────────────────
    ctx.strokeStyle = "rgba(54,196,168,0.2)";
    ctx.lineWidth   = 0.5;
    ctx.beginPath(); ctx.moveTo(cx - maxR * 0.5, cy); ctx.lineTo(cx + maxR * 0.5, cy); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, cy - maxR * 0.5); ctx.lineTo(cx, cy + maxR * 0.5); ctx.stroke();

    // ── blueprint corner marks ───────────────────────────────
    const corners = [[32, 32, 1, 1], [W - 32, 32, -1, 1], [32, H - 32, 1, -1], [W - 32, H - 32, -1, -1]];
    ctx.strokeStyle = "rgba(54,196,168,0.35)";
    ctx.lineWidth   = 1;
    corners.forEach(([x, y, sx, sy]) => {
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + sx * 22, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y + sy * 22); ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + sx * 5, y + sy * 5, 2, 0, Math.PI * 2);
      ctx.stroke();
    });

    // ── telemetry text ───────────────────────────────────────
    ctx.font         = `11px ${MONO}`;
    ctx.letterSpacing = "0.2em";
    ctx.fillStyle    = "rgba(54,196,168,0.45)";
    // Top-left
    ctx.fillText(`LAT  ${s.lat}`, 52, 54);
    ctx.fillText(`LON  ${s.lon}`, 52, 70);
    // Top-right
    const sessionStr = `${s.session}  ◌  LIVE`;
    ctx.textAlign = "right";
    ctx.fillText(sessionStr, W - 52, 54);
    ctx.fillText(`T+ ${((now - (startRef.current || now)) / 1000).toFixed(2)}s`, W - 52, 70);
    ctx.textAlign = "left";

    // Phase label — centered below
    ctx.font      = `11px ${MONO}`;
    ctx.fillStyle = TEAL;
    ctx.textAlign = "center";
    const phaseStr = PHASES[Math.min(s.phase, PHASES.length - 1)];
    const dots = ".".repeat(Math.floor(now / 300) % 4);
    ctx.fillText(`[ ${phaseStr}${dots} ]`, cx, cy + maxR + 48);
    ctx.textAlign = "left";

    // Progress percentage
    const pct = Math.round(s.arcT * 100);
    ctx.font      = `10px ${MONO}`;
    ctx.fillStyle = "rgba(54,196,168,0.35)";
    ctx.textAlign = "center";
    ctx.fillText(`${pct}%`, cx, cy + maxR + 64);
    ctx.textAlign = "left";

    // ── node labels on rings ─────────────────────────────────
    if (s.rings >= 5) {
      const nodeAngles = [0.2, 1.1, 2.2, 3.8, 4.9];
      const nodeRing   = ringRadii[3];
      nodeAngles.forEach((a, i) => {
        const nx = cx + Math.cos(a) * nodeRing;
        const ny = cy + Math.sin(a) * nodeRing;
        ctx.beginPath();
        ctx.arc(nx, ny, 3, 0, Math.PI * 2);
        ctx.fillStyle = i === 2 ? TEAL : "rgba(54,196,168,0.5)";
        if (i === 2) { ctx.shadowBlur = 10; ctx.shadowColor = TEAL; }
        ctx.fill();
        ctx.shadowBlur = 0;
      });
    }
  }

  // ── orchestrate phases ───────────────────────────────────
  useEffect(() => {
    startRef.current = performance.now();
    const s = stateRef.current;

    // 1. Draw rings one by one
    let ringCount = 0;
    const ringTimer = setInterval(() => {
      ringCount++;
      stateRef.current.rings = ringCount;
      if (ringCount >= 6) clearInterval(ringTimer);
    }, RING_INTERVAL);

    // 2. Cycle phase labels
    let phaseIdx = 0;
    const phaseTimer = setInterval(() => {
      phaseIdx = (phaseIdx + 1) % PHASES.length;
      stateRef.current.phase = phaseIdx;
      setPhase(phaseIdx); // also update React state for re-render if needed
    }, PHASE_INTERVAL);

    // 3. Progress arc fill
    const arcStart = performance.now() + RING_INTERVAL * 4;
    const arcTick = () => {
      const elapsed = performance.now() - arcStart;
      const t = Math.max(0, Math.min(1, elapsed / ARC_DURATION));
      stateRef.current.arcT = t;
      if (t < 1) requestAnimationFrame(arcTick);
      else {
        // 4. Begin curtain wipe
        stateRef.current.wiping = true;
        stateRef.current.phase  = PHASES.length - 1;
        clearInterval(phaseTimer);
        const wipeStart = performance.now();
        const wipeTick  = () => {
          const wt = Math.min(1, (performance.now() - wipeStart) / WIPE_DURATION);
          stateRef.current.wipeT = wt;
          if (wt < 1) requestAnimationFrame(wipeTick);
          else {
            stateRef.current.done = true;
            cancelAnimationFrame(rafRef.current);
            if (onComplete) onComplete();
          }
        };
        requestAnimationFrame(wipeTick);
      }
    };
    requestAnimationFrame(arcTick);

    // 5. Coord flicker
    const coordTimer = setInterval(() => {
      const jitter = () => (Math.random() * 0.1).toFixed(2);
      stateRef.current.lat = `N 40°${(43 + parseFloat(jitter())).toFixed(2)}′`;
      stateRef.current.lon = `W 74°${(0 + parseFloat(jitter())).toFixed(2)}′`;
    }, TICKER_INTERVAL);

    // Start draw loop
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      clearInterval(ringTimer);
      clearInterval(phaseTimer);
      clearInterval(coordTimer);
      cancelAnimationFrame(rafRef.current);
    };
  }, [draw, onComplete]);

  // ── resize handler ───────────────────────────────────────
  useEffect(() => {
    const resize = () => {
      if (!canvasRef.current) return;
      canvasRef.current.width  = window.innerWidth;
      canvasRef.current.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: BG,
      pointerEvents: "none",
    }}>
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
    </div>
  );
}
